// 32 avatars par défaut (doc 05 §6.2) — direction visuelle : « objets et animaux de fête
// dessinés au trait 2 px sur pastille pleine », le trait reprend le langage Lucide.
// Un seul set, cohérent : viewBox 24, `fill="none"`, `stroke="currentColor"`,
// `stroke-width="2"`, bouts et jointures arrondis. Les seuls remplissages sont les
// pupilles/points (`class="f"`, remplacé par `fill="currentColor" stroke="none"`).
//
// Source de vérité : ce fichier. `scripts/build-assets.ts` en dérive
// `assets/avatars/avatar-01-disco.svg` … et le spritesheet `assets/avatars/sprite.svg`.
// Les surfaces web (join, display) inlinent le sprite : zéro requête (doc 03 §4.1).

export interface AvatarDef {
  /** Clé stockée en base (`participants.avatar_builtin_key`) — jamais renumérotée. */
  key: string;
  /** Contenu du `<svg viewBox="0 0 24 24">`. */
  body: string;
}

/** Marqueur de remplissage : un point plein (œil, pépite) au milieu d'un dessin au trait. */
const F = 'fill="currentColor" stroke="none"';

export const avatars = [
  {
    key: 'disco',
    body: `<circle cx="12" cy="13" r="7"/><path d="M5 13h14M12 6v14M7.1 8.1 16.9 17.9M16.9 8.1 7.1 17.9M12 6V3M9 3h6"/>`,
  },
  {
    key: 'mic',
    body: `<rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v4M8.5 22h7"/>`,
  },
  {
    key: 'dice',
    body: `<rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="8.5" cy="8.5" r="1.3" ${F}/><circle cx="12" cy="12" r="1.3" ${F}/><circle cx="15.5" cy="15.5" r="1.3" ${F}/>`,
  },
  {
    key: 'vinyl',
    body: `<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5"/><circle cx="12" cy="12" r="1" ${F}/>`,
  },
  {
    key: 'cocktail',
    body: `<path d="M4 5h16l-8 8.5z M12 13.5V19M8 19h8M17.5 3.5 14 7.5"/><circle cx="18.2" cy="2.8" r="1.4"/>`,
  },
  {
    key: 'trophy',
    body: `<path d="M7 3h10v5.5a5 5 0 0 1-10 0z M7 5H4a3.2 3.2 0 0 0 3 3.4M17 5h3a3.2 3.2 0 0 1-3 3.4M12 13.5V17M8.5 21h7l-1-4h-5z"/>`,
  },
  { key: 'bolt', body: `<path d="M13.6 2 4.6 14.2h6.1L9.9 22l9.5-12.4h-6.4z"/>` },
  {
    key: 'flamingo',
    body: `<path d="M11.4 6.4a2.4 2.4 0 1 1 2.6 2.3M14 8.7l3.4.8-3.4 1.4M12.9 10.6C12.2 12.4 11 14 9.8 14.8"/><ellipse cx="10.5" cy="16.5" rx="5.5" ry="3.6"/><path d="M9 20v2.2M12.5 20v2M7.4 22.2h3.2"/>`,
  },
  {
    key: 'octopus',
    body: `<path d="M5.5 12a6.5 6.5 0 0 1 13 0v2.5h-13z"/><circle cx="9.8" cy="11" r="1" ${F}/><circle cx="14.2" cy="11" r="1" ${F}/><path d="M6.5 14.5c-.5 2.5-1.5 3.5-3 4.5M10 14.5c-.5 3-1 4.5-2 6M14 14.5c.5 3 1 4.5 2 6M17.5 14.5c.5 2.5 1.5 3.5 3 4.5"/>`,
  },
  {
    key: 'fox',
    body: `<path d="M3.6 7.4 5.7 2.5l4.3 3h4l4.3-3 2.1 4.9c0 6.4-4.2 11.1-6.4 11.1h-4c-2.2 0-6.4-4.7-6.4-11.1z"/><circle cx="9.3" cy="11" r="1" ${F}/><circle cx="14.7" cy="11" r="1" ${F}/><path d="M10.7 14.4h2.6L12 16.2z"/>`,
  },
  {
    key: 'panda',
    body: `<circle cx="6.2" cy="6.4" r="2.8"/><circle cx="17.8" cy="6.4" r="2.8"/><circle cx="12" cy="13.5" r="7.2"/><ellipse cx="9.2" cy="12.2" rx="1.7" ry="2.2" ${F}/><ellipse cx="14.8" cy="12.2" rx="1.7" ry="2.2" ${F}/><circle cx="12" cy="15.8" r="1.1" ${F}/>`,
  },
  {
    key: 'cactus',
    body: `<rect x="9.8" y="2.5" width="4.4" height="16" rx="2.2"/><path d="M9.8 12H8a2.6 2.6 0 0 1-2.6-2.6V7.6M14.2 9.6H16a2.6 2.6 0 0 0 2.6-2.6V5.8M7.4 18.5h9.2l-1 3.5H8.4z"/>`,
  },
  {
    key: 'rocket',
    body: `<path d="M12 2c3.1 2.7 4.7 6.3 4.7 9.9L14.2 15H9.8l-2.5-3.1C7.3 8.3 8.9 4.7 12 2z"/><circle cx="12" cy="9" r="1.9"/><path d="M9.6 14 6 17.2l3.2-.4M14.4 14 18 17.2l-3.2-.4M10.4 17.6 12 22l1.6-4.4"/>`,
  },
  {
    key: 'ghost',
    body: `<path d="M4.8 20.5V10a7.2 7.2 0 0 1 14.4 0v10.5l-2.4-2-2.4 2-2.4-2-2.4 2z"/><circle cx="9.6" cy="10" r="1.1" ${F}/><circle cx="14.4" cy="10" r="1.1" ${F}/>`,
  },
  {
    key: 'donut',
    body: `<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.2"/><path d="M8.4 6.4 7.2 8M15.4 6.6 16.8 8M18.2 12.6 19.4 14M5.5 13.2 4.4 14.8M11 19.4l1.8.6"/>`,
  },
  {
    key: 'maracas',
    body: `<ellipse cx="8" cy="7.5" rx="4" ry="4.5"/><path d="M8 12v8M6 20h4"/><ellipse cx="17.5" cy="10" rx="3" ry="3.4"/><path d="M17.5 13.4V20M15.9 20h3.2"/>`,
  },
  {
    key: 'guitar',
    body: `<ellipse cx="9" cy="16" rx="5.6" ry="5"/><circle cx="9" cy="16" r="1.6"/><path d="M12.6 12.3 19 5.9M17.6 4.5 20.5 7.4"/>`,
  },
  {
    key: 'drum',
    body: `<ellipse cx="12" cy="7.5" rx="8" ry="3"/><path d="M4 7.5v8c0 1.7 3.6 3 8 3s8-1.3 8-3v-8"/><path d="M4.5 10 8 13.2 12 10l4 3.2 3.5-3.2"/>`,
  },
  {
    key: 'headphones',
    body: `<path d="M4 15.5v-3.2a8 8 0 0 1 16 0v3.2"/><rect x="2" y="14" width="4.4" height="7" rx="2.2"/><rect x="17.6" y="14" width="4.4" height="7" rx="2.2"/>`,
  },
  {
    key: 'star',
    body: `<path d="M12 2.8 14.8 8.6l6.4.9-4.6 4.5 1.1 6.4L12 17.3l-5.7 3.1 1.1-6.4-4.6-4.5 6.4-.9z"/>`,
  },
  {
    key: 'crown',
    body: `<path d="M3.5 18 2.6 6.6l5.7 4.2L12 3.4l3.7 7.4 5.7-4.2L20.5 18z M4 21.2h16"/>`,
  },
  {
    key: 'balloon',
    body: `<ellipse cx="12" cy="9" rx="6" ry="7"/><path d="M10.6 15.6 12 17.6l1.4-2M12 17.6c0 2.6 2 2.8 2 4.4"/>`,
  },
  {
    key: 'cupcake',
    body: `<path d="M5.8 11h12.4l-1.6 9.5H7.4z M5.8 11a3 3 0 0 1 1.1-5.7 4.2 4.2 0 0 1 8.1-1 3 3 0 0 1 3 6.7"/><circle cx="12" cy="2.8" r="1.3"/>`,
  },
  {
    key: 'pizza',
    body: `<path d="M12 2.6 21.3 20a24 24 0 0 1-18.6 0z"/><circle cx="10" cy="12.4" r="1.2" ${F}/><circle cx="14.2" cy="15" r="1.2" ${F}/><circle cx="11.4" cy="17.6" r="1.2" ${F}/>`,
  },
  {
    key: 'popcorn',
    body: `<path d="M7 11h10l-1.2 10.4H8.2z M12 11v10.4"/><circle cx="8.6" cy="7.6" r="2.4"/><circle cx="12" cy="5.8" r="2.6"/><circle cx="15.4" cy="7.6" r="2.4"/>`,
  },
  {
    key: 'whale',
    body: `<path d="M2.4 13.4C3.6 9.2 7.4 6.6 11.6 6.6c4.8 0 8.6 3 8.6 6.9 0 3-2.6 4.9-6 4.9H7.4c-3 0-5.7-2-5-5z"/><path d="M20.2 12.6 23 10v7.2l-2.4-2M13 3.4c0 2 1 2.2 1 3.2"/><circle cx="8" cy="12.4" r="1" ${F}/>`,
  },
  {
    key: 'penguin',
    body: `<path d="M12 2.2a6 6 0 0 1 6 6v7.4a6 6 0 0 1-12 0V8.2a6 6 0 0 1 6-6z"/><path d="M12 6.6a3.4 3.4 0 0 1 3.4 3.4v5a3.4 3.4 0 0 1-6.8 0v-5A3.4 3.4 0 0 1 12 6.6z"/><circle cx="10.2" cy="7.6" r=".9" ${F}/><circle cx="13.8" cy="7.6" r=".9" ${F}/><path d="M10.8 9.4h2.4L12 11z M7.4 21.6h3.2M13.4 21.6h3.2"/>`,
  },
  {
    key: 'owl',
    body: `<path d="M4.4 11a7.6 7.6 0 0 1 15.2 0v3.4a7.6 7.6 0 0 1-15.2 0z"/><circle cx="9" cy="11" r="2.4"/><circle cx="15" cy="11" r="2.4"/><circle cx="9" cy="11" r="1" ${F}/><circle cx="15" cy="11" r="1" ${F}/><path d="M10.8 14.2h2.4L12 16z M5.2 5.2 8 7.6M18.8 5.2 16 7.6"/>`,
  },
  {
    key: 'cat',
    body: `<path d="M4.6 9.4 5.8 3.6l4.2 2.8h4l4.2-2.8 1.2 5.8v4.4a7.4 7.4 0 0 1-14.8 0z"/><circle cx="9.4" cy="11.6" r="1" ${F}/><circle cx="14.6" cy="11.6" r="1" ${F}/><path d="M11 14h2l-1 1.4z M2.6 13.2h3.4M18 13.2h3.4"/>`,
  },
  {
    key: 'robot',
    body: `<rect x="4" y="7" width="16" height="12.5" rx="3.5"/><circle cx="9" cy="12" r="1.4" ${F}/><circle cx="15" cy="12" r="1.4" ${F}/><path d="M12 7V4M9 16h6M4 12.5H1.8M20 12.5h2.2"/><circle cx="12" cy="2.8" r="1.3"/>`,
  },
  {
    key: 'alien',
    body: `<path d="M12 2.2c4.9 0 8 3.7 8 8.2 0 6-4 11.4-8 11.4S4 16.4 4 10.4c0-4.5 3.1-8.2 8-8.2z"/><ellipse cx="8.8" cy="10.6" rx="1.9" ry="2.8" ${F}/><ellipse cx="15.2" cy="10.6" rx="1.9" ry="2.8" ${F}/><path d="M10 17.2h4"/>`,
  },
  {
    key: 'unicorn',
    body: `<path d="M12.6 8.6 11 2.4l4.4 4.6"/><path d="M7 21.4v-5.2a6 6 0 0 1 6-6h3.2l3.2 3-2 2.2v6"/><circle cx="15.4" cy="14" r="1" ${F}/><path d="M7.6 12.4C5.4 13.4 4 15.6 4 18.2"/>`,
  },
] as const satisfies readonly AvatarDef[];

export type AvatarKey = (typeof avatars)[number]['key'];

export const AVATAR_KEYS = avatars.map((a) => a.key) as AvatarKey[];

/** Nom de fichier canonique : `avatar-01-disco.svg` (doc 05 §6.2). */
export function avatarFileName(index: number): string {
  const entry = avatars[index];
  if (entry === undefined) {
    throw new Error(`Unknown avatar index: ${index}`);
  }
  return `avatar-${String(index + 1).padStart(2, '0')}-${entry.key}.svg`;
}

export function avatarBody(key: string): string {
  return avatars.find((a) => a.key === key)?.body ?? avatars[0].body;
}

/** Clé d'avatar présélectionnée au hasard (doc 03 §4.1 : valider sans toucher). */
export function randomAvatarKey(random: () => number = Math.random): AvatarKey {
  return AVATAR_KEYS[Math.floor(random() * AVATAR_KEYS.length)] as AvatarKey;
}
